/**
 * Gets audio format fallbacks for a given audio path
 * Returns array of potential audio file paths to try
 */
export function getAudioFormatFallbacks(audioPath: string): string[] {
  const basePath = audioPath.replace(/\.(mp3|ogg|wav|m4a|aac)$/i, '');
  const fallbacks = [
    `${basePath}.mp3`,
    `${basePath}.m4a`,
    `${basePath}.aac`,
    `${basePath}.ogg`,
    `${basePath}.wav`,
  ];

  const uniqueFallbacks = Array.from(new Set(fallbacks));

  if (!uniqueFallbacks.includes(audioPath)) {
    return [audioPath, ...uniqueFallbacks];
  }

  return uniqueFallbacks;
}

/**
 * Creates an audio element and tests if it can load
 */
function testAudioLoad(audioPath: string): Promise<boolean> {
  return new Promise((resolve) => {
    const testAudio = new Audio(audioPath);
    let resolved = false;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const cleanup = () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }

      testAudio.oncanplaythrough = null;
      testAudio.onerror = null;
      testAudio.src = '';
    };

    const onCanPlay = () => {
      if (resolved) return;

      resolved = true;
      cleanup();
      resolve(true);
    };

    const onError = () => {
      if (resolved) return;

      resolved = true;
      cleanup();
      resolve(false);
    };

    testAudio.oncanplaythrough = onCanPlay;
    testAudio.onerror = onError;
    testAudio.load();

    timeoutId = setTimeout(() => {
      if (!resolved) {
        resolved = true;
        cleanup();
        resolve(false);
      }
    }, 3000);
  });
}

/**
 * Finds a working audio format from fallbacks
 */
export async function findWorkingAudioFormat(
  audioPath: string
): Promise<string | null> {
  const fallbacks = getAudioFormatFallbacks(audioPath);

  const testResults = await Promise.all(
    fallbacks.map((fallbackPath) => testAudioLoad(fallbackPath))
  );

  const workingIndex = testResults.findIndex((canLoad) => canLoad);

  if (workingIndex >= 0) {
    return fallbacks[workingIndex] || null;
  }

  return null;
}
